import { T, tt } from '@agos/shared'
import { cn } from '@agos/ui'
import { CheckCircle2 } from 'lucide-react'

import {
  extractPizzaSize,
  giftNameOf,
  isGarlicCrust,
  isGiftItem,
  mergeIngredientsAndExtras,
  ingredientChipClasses,
  ingredientCountClasses,
  KITCHEN_TEXT,
  parseItemName,
  partsDescription,
  type MergedIngredient,
} from './item-name'
import type { ItemProgress } from './elapsed'
import { bakesAsPizza, productValue, splitAfterBaking } from './kitchen-descriptions'
import { formatAmount, type RecipeIngredient } from './recipes'
import type { KitchenLine } from './kitchen-items'
import { KitchenLineNote } from './KitchenLineNote'

/**
 * One line on a kitchen card, and the thing a cook actually taps.
 *
 * Tapping cycles it through untouched → being made → made. Since §9.811 (owner
 * O157) that state is stored on the line, so every tablet shows it and a refresh
 * keeps it; this card only draws what the board hands it.
 */

/** Product id → the kitchen-only description the menu editor wrote. */
export interface KitchenDescriptions {
  byId: Record<number, string>
  byName: Record<string, string>
  /** Product id → whether its description may be shown at all. */
  showInKitchen: Record<number, boolean>
  /**
   * Product id → its recipe's ingredients — `FRONTEND.md` §9.55, the owner's *"malo by byť vidieť
   * u každého produktu to, čo je nastavené ako RECEPT"*.
   *
   * **Kept beside the prose description rather than replacing it, and the data is why.** Measured on
   * the restored menu: 87 products have a recipe, 95 have a description, and the two overlap on only
   * 44. Preferring the recipe alone would blank 51 products that have only prose; keeping only the
   * prose leaves 43 showing a bare name, which is what the kitchen does today. Together they cover
   * 138 of 173.
   */
  recipes: Record<number, RecipeIngredient[]>
  /** Lower-cased product name → the same, for a kombo half that is only a name. */
  recipesByName: Record<string, RecipeIngredient[]>
  /**
   * Product id -> a dynamická pizza's composition, the ingredients ticked in *Výber ingrediencií* - §9.671, owner
   * #1. Ahead of the recipe and the description, because it is what the pizza is priced and made from.
   *
   * **Optional**, because absent means *no dynamic pizza*, and every fixture that builds this record by hand - in
   * this lane and in the lanes working beside it - stays a valid one.
   */
  configured?: Record<number, string>
  /** Lower-cased product name -> the same, for a kombo half that is only a name. */
  configuredByName?: Record<string, string>
  /**
   * Folded names of the ingredients that go on after baking - §9.673, owner #7. Their chips are drawn below a line
   * labelled *Po upečení*. Optional for the same reason as `configured`: absent means nothing is flagged.
   */
  afterBaking?: ReadonlySet<string>
  /**
   * Product id -> whether it goes into the oven as a pizza - §9.763, owner O125. Only a pizza's card splits at
   * *Po upečení*; a beef bowl naming *Rukola* draws its chips in one list. Read through `bakesAsPizza`. Optional for
   * the same reason as `configured`: absent means no catalogue product is a pizza.
   */
  pizzas?: Record<number, boolean>
}

/** Strips whatever size badge was pulled out, so the name is not repeated. */
const SIZE_IN_NAME =
  /^\d{2,3}\s?cm\s*|\s*[-–—]+\s*\d{2,3}\s?cm|\s*[([]\s*\d{2,3}\s?cm\s*[)\]]|\s+\d{2,3}\s?cm/gi

export function KitchenItemCard({
  line,
  descriptions,
  progress,
  onToggle,
  addon = false,
}: {
  line: KitchenLine
  descriptions: KitchenDescriptions
  progress: ItemProgress
  onToggle: () => void
  /** §9.713 - drawn under the line it was ordered with, so it reads *+ Hranolky*. */
  addon?: boolean
}) {
  const productId = line.productId ?? null

  // §9.671 - each table is read under the product's own id, and under its legacy size variant's
  // (`p * 1000 + n`) only when the id is not itself a product: a real product numbered 1 000 or more
  // used to borrow product 1's flag, description and recipe.
  const mayShowDescription =
    !productId || productValue(descriptions.showInKitchen, productId, descriptions) !== false

  const kitchenDescription =
    mayShowDescription && productId
      ? (productValue(descriptions.byId, productId, descriptions) ?? null)
      : null

  // The recipe is preferred where there is one: where both exist they say the same thing, and the
  // recipe is the one carrying weights — *Trhané hovädzie 150 g* rather than *trhané hovädzie*.
  const recipe =
    mayShowDescription && productId
      ? (productValue(descriptions.recipes, productId, descriptions) ?? null)
      : null

  // §9.671, owner #1 - a dynamická pizza is made of what *Výber ingrediencií* ticks, so that comes
  // ahead of a recipe or a description somebody wrote separately.
  const configured =
    mayShowDescription && productId
      ? (productValue(descriptions.configured ?? {}, productId, descriptions) ?? null)
      : null

  const gift = isGiftItem(line.productName)
  const rawName = gift ? giftNameOf(line.productName) : (line.productName ?? '')
  const pizzaSize = extractPizzaSize(rawName)
  const parsed = parseItemName(pizzaSize ? rawName.replace(SIZE_IN_NAME, '').trim() : rawName)

  // §9.667, owner O51 - *"In Sereď - kuchyňa, we dont see zloženie dynamických píz."* The parse above
  // finds a composition only where the order builder put one inside parentheses; the parts are a
  // column and are there whoever wrote the line. Behind the parse rather than instead of it, so a name
  // that does carry its list still prints exactly what the till printed.
  const storedComposition = parsed.description === null ? partsDescription(line.parts) : null

  // §9.763, owner O125 - *Po upečení* is a pizza's line: a pizza, a dynamická or custom pizza, each kombo half.
  // Every other card draws its chips in one list, as before §9.673.
  const afterBaking = bakesAsPizza(line, descriptions) ? descriptions.afterBaking : undefined

  const isDone = progress === 'done'
  const isWorking = progress === 'working'

  return (
    <button
      type="button"
      onClick={onToggle}
      {...(addon ? { 'data-kitchen-addon': line.id ?? '' } : {})}
      className={cn(
        'w-full text-left rounded-lg border transition-all duration-200 px-2.5 py-2 flex items-start gap-2',
        isDone
          ? 'bg-emerald-500/10 border-emerald-500/25 opacity-50'
          : isWorking
            ? 'bg-yellow-500/10 border-yellow-500/30 ring-1 ring-yellow-500/40'
            : 'bg-slate-900/3 dark:bg-white/3 border-slate-200 dark:border-white/8 hover:bg-slate-900/6 dark:hover:bg-white/6',
        gift && !isWorking && !isDone && 'bg-amber-500/8 border-amber-500/15',
      )}
    >
      <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
        <span
          className={cn(
            'min-w-[32px] h-[32px] rounded font-black flex items-center justify-center leading-none',
            KITCHEN_TEXT.quantity,
            isDone
              ? 'bg-emerald-500/25 text-emerald-700 dark:text-emerald-400'
              : isWorking
                ? 'bg-yellow-500/25 text-yellow-700 dark:text-yellow-300'
                : gift
                  ? 'bg-amber-500/25 text-amber-700 dark:text-amber-400'
                  : (line.quantity ?? 1) >= 2
                    ? 'bg-orange-500/30 text-orange-700 dark:text-orange-300'
                    : 'bg-slate-900/10 dark:bg-white/10 text-slate-800 dark:text-white/90',
          )}
        >
          {line.quantity}×
        </span>
        {isDone && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 dark:text-emerald-400" />}
        {isWorking && <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />}
      </div>

      <div className="flex flex-col min-w-0 gap-0.5 flex-1">
        {gift && (
          <span className={cn(KITCHEN_TEXT.label, 'font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider')}>
            <T>🎁 Darček</T>
          </span>
        )}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className={cn(
              KITCHEN_TEXT.name,
              'font-black leading-tight',
              isDone ? 'text-slate-500 dark:text-white/40 line-through' : 'text-slate-900 dark:text-white',
            )}
          >
            {addon && '+ '}
            <T>{parsed.name}</T>
          </span>
          {pizzaSize && (
            <span
              className={cn(
                KITCHEN_TEXT.size,
                'font-black px-1.5 py-0.5 rounded bg-orange-500/25 text-orange-700 dark:text-orange-300 leading-none flex-shrink-0',
              )}
            >
              {pizzaSize}
            </span>
          )}
        </div>

        {parsed.isKombo && parsed.description ? (
          <KomboHalves
            halves={parsed.description}
            descriptions={descriptions}
            afterBaking={afterBaking}
          />
        ) : configured ? (
          <SingleIngredients
            description={configured}
            extras={parsed.extras}
            afterBaking={afterBaking}
          />
        ) : recipe && recipe.length > 0 ? (
          <RecipeList
            ingredients={recipe}
            extras={parsed.extras}
            afterBaking={afterBaking}
          />
        ) : (
          mayShowDescription && (
            <SingleIngredients
              // §9.667 - the stored parts come last, so a kitchen description somebody wrote and a
              // list the till printed both still win. It answers only where neither exists.
              description={kitchenDescription ?? parsed.description ?? storedComposition}
              extras={parsed.extras}
              afterBaking={afterBaking}
            />
          )
        )}

        {/* §9.691, owner #3 - what somebody wrote about this one item, under it. */}
        <KitchenLineNote line={line} done={isDone} />
      </div>
    </button>
  )
}

/**
 * A kombo is two pizzas in one box, and the cook needs both recipes. Legacy
 * looks each half up by name, because the halves are only names in the line.
 */
function KomboHalves({
  halves,
  descriptions,
  afterBaking,
}: {
  halves: string
  descriptions: KitchenDescriptions
  /** §9.763 - the card's own answer; a kombo is pizzas, so it arrives set whenever a topping is flagged. */
  afterBaking: ReadonlySet<string> | undefined
}) {
  return (
    <div className="flex flex-col gap-0.5 mt-0.5">
      {halves.split(' · ').map((half, position) => {
        const name = half.trim()
        // Same preference as a single line: a dynamická pizza's own composition (§9.671), then the
        // recipe where there is one, the prose otherwise.
        const halfConfigured = descriptions.configuredByName?.[name.toLowerCase()] ?? null
        const halfRecipe = halfConfigured
          ? null
          : (descriptions.recipesByName[name.toLowerCase()] ?? null)
        const prose = halfConfigured ?? descriptions.byName[name.toLowerCase()] ?? null
        const merged = prose ? mergeIngredientsAndExtras(prose, null) : []
        return (
          // §9.406 - **keyed by position, not by name.** Measured on the owner's data: all 140 of
          // their kombo lines carry three pizzas and **24 repeat one**, so `key={name}` was a
          // duplicate key on one card in six - which React resolves by reusing an element, so a
          // change to one half can paint on the other.
          <div
            key={position}
            className="flex flex-col gap-0.5 pl-1 border-l-2 border-orange-500/30"
            data-kombo-half={position + 1}
          >
            <span className={cn(KITCHEN_TEXT.half, 'font-black text-slate-800 dark:text-white/80 leading-tight')}>
              {/* §9.406, the owner's ask: *Pizza 1 / Pizza 2 / Pizza 3*. A number rather than the
                * word, because the header above already says *Kombo Pizza* and a card is read
                * across a kitchen. On the 24 lines with a repeated pizza it is the only thing that
                * says two identical names are two pizzas. */}
              <span className="text-orange-700/80 dark:text-orange-400/80">{position + 1}.</span> {name}
            </span>
            {/* §9.673 - each half is a pizza of its own, so each draws its own line. */}
            {halfRecipe && halfRecipe.length > 0 ? (
              <RecipeList
                ingredients={halfRecipe}
                extras={null}
                afterBaking={afterBaking}
              />
            ) : (
              merged.length > 0 && (
                <IngredientsList merged={merged} afterBaking={afterBaking} />
              )
            )}
          </div>
        )
      })}
    </div>
  )
}

/**
 * The line a cook reads as *everything below goes on once it is out of the oven* - §9.673, owner #7.
 *
 * *"pozri si fotku tam je čiara a pod ňu sú veci ktoré majú ísť po upeceni"* - a line, and the words, because a
 * line alone on a card that has never had one reads as a layout glitch.
 */
function AfterBakingDivider() {
  return (
    <div data-after-baking className="flex items-center gap-1.5 mt-1">
      <span className="h-px flex-1 bg-sky-300/50" />
      <span className={cn(KITCHEN_TEXT.label, 'font-black uppercase tracking-widest text-sky-700 dark:text-sky-300')}>
        <T>Po upečení</T>
      </span>
      <span className="h-px flex-1 bg-sky-300/50" />
    </div>
  )
}

/**
 * A recipe, as weights rather than prose - §9.55.
 *
 * Anything the customer added on top still comes from the line's own `[extras]`, because an extra is
 * not part of the recipe by definition: it is what this customer asked for beyond it.
 *
 * §9.673 - the recipe and its extras share **one** line: an extra rukola belongs below it with the
 * recipe's own, not under a second divider of its own.
 */
function RecipeList({
  ingredients,
  extras,
  afterBaking,
}: {
  ingredients: RecipeIngredient[]
  extras: string | null
  afterBaking: ReadonlySet<string> | undefined
}) {
  const recipe = splitAfterBaking(ingredients, (ingredient) => ingredient.name, afterBaking)
  const added = splitAfterBaking(
    extras ? mergeIngredientsAndExtras(null, extras) : [],
    (ingredient) => ingredient.name,
    afterBaking,
  )
  return (
    <div className="flex flex-col gap-0.5 mt-0.5">
      {recipe.before.map((ingredient, index) => (
        // Ingredient names repeat within a recipe; the index is what keeps two rows distinct.
        <RecipeChip key={`before-${ingredient.name}-${index}`} ingredient={ingredient} />
      ))}
      {added.before.map((ingredient, index) => (
        <IngredientChip key={`added-${ingredient.name}-${index}`} ingredient={ingredient} />
      ))}
      {(recipe.after.length > 0 || added.after.length > 0) && <AfterBakingDivider />}
      {recipe.after.map((ingredient, index) => (
        <RecipeChip key={`after-${ingredient.name}-${index}`} ingredient={ingredient} />
      ))}
      {added.after.map((ingredient, index) => (
        <IngredientChip key={`added-after-${ingredient.name}-${index}`} ingredient={ingredient} />
      ))}
    </div>
  )
}

function RecipeChip({ ingredient }: { ingredient: RecipeIngredient }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded px-1.5 py-0.5 leading-tight',
        KITCHEN_TEXT.chip,
        ingredientChipClasses({}),
      )}
    >
      {formatAmount(ingredient) !== null && (
        <span className={ingredientCountClasses({})}>{formatAmount(ingredient)}</span>
      )}
      <T>{ingredient.name}</T>
    </span>
  )
}

function SingleIngredients({
  description,
  extras,
  afterBaking,
}: {
  description: string | null
  extras: string | null
  afterBaking: ReadonlySet<string> | undefined
}) {
  if (!description && !extras) return null
  return (
    <IngredientsList
      merged={mergeIngredientsAndExtras(description, extras)}
      afterBaking={afterBaking}
    />
  )
}

function IngredientsList({
  merged,
  afterBaking,
}: {
  merged: MergedIngredient[]
  afterBaking: ReadonlySet<string> | undefined
}) {
  const split = splitAfterBaking(merged, (ingredient) => ingredient.name, afterBaking)
  return (
    <div className="flex flex-col gap-0.5 mt-0.5">
      {split.before.map((ingredient, index) => (
        // Ingredient names repeat across an order; the index is what makes
        // two chips reading the same thing distinct.
        <IngredientChip key={`before-${ingredient.name}-${index}`} ingredient={ingredient} />
      ))}
      {split.after.length > 0 && <AfterBakingDivider />}
      {split.after.map((ingredient, index) => (
        <IngredientChip key={`after-${ingredient.name}-${index}`} ingredient={ingredient} />
      ))}
    </div>
  )
}

function IngredientChip({ ingredient }: { ingredient: MergedIngredient }) {
  const garlic = isGarlicCrust(ingredient.name)
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded px-1.5 py-0.5 leading-tight',
        KITCHEN_TEXT.chip,
        // §9.445 - `boosted` too. It was reaching `ingredientCountClasses` alone, so a
        // doubled recipe ingredient got a red digit on an ordinary chip while a plain
        // addition got a red chip.
        ingredientChipClasses({
          garlic,
          standalone: ingredient.standalone,
          boosted: ingredient.boosted,
        }),
      )}
    >
      {garlic && <span aria-label={tt('cesnakové kraje')}>🧄</span>}
      <span className={ingredientCountClasses({ garlic, boosted: ingredient.boosted })}>
        {ingredient.count}×
      </span>
      <T>{ingredient.name}</T>
    </span>
  )
}
